import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Application {
  id:                   string;
  application_number:   string;
  status:               string;
  applied_level:        string | null;
  student_full_name_en: string;
  student_ic_number:    string | null;
  guardian_name:        string;
  guardian_phone:       string;
  school_name:          string | null;
  submitted_at:         string | null;
}

interface PageResult {
  data:  Application[];
  total: number;
  page:  number;
  limit: number;
}

const STATUS_LABEL: Record<string, { en: string; ms: string; color: string }> = {
  draft:          { en: 'Draft',          ms: 'Draf',            color: colors.neutral.gray400 },
  submitted:      { en: 'Submitted',      ms: 'Dihantar',        color: '#60a5fa' },
  pending_review: { en: 'Pending Review', ms: 'Menunggu Semakan', color: '#facc15' },
  approved:       { en: 'Approved',       ms: 'Diluluskan',      color: '#4ade80' },
  rejected:       { en: 'Rejected',       ms: 'Ditolak',         color: '#f87171' },
  withdrawn:      { en: 'Withdrawn',      ms: 'Ditarik Balik',   color: colors.neutral.gray600 },
};

const LEVELS = ['', 'primary', 'secondary', 'upper-secondary'];

export function ApplicationsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [data,    setData]    = useState<Application[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterLevel,  setFilterLevel]  = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchInput,  setSearchInput]  = useState('');

  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (filterStatus) params.set('status', filterStatus);
      if (filterLevel)  params.set('appliedLevel', filterLevel);
      if (filterSearch) params.set('search', filterSearch);

      const result = await apiGet<PageResult>(`/api/students/applications?${params}`);
      setData(result.data);
      setTotal(result.total);
    } catch (err) {
      const msg = err instanceof TypeError && err.message.includes('fetch')
        ? 'Tidak dapat menghubungi pelayan svc-sis. / Cannot reach svc-sis server.'
        : err instanceof Error ? err.message : 'Ralat tidak diketahui.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterLevel, filterSearch]);

  useEffect(() => { void load(); }, [load]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setFilterSearch(searchInput.trim());
    setPage(1);
  }

  function handleFilterChange() {
    setPage(1);
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      {/* Page heading */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={{ fontFamily:'"Noto Serif",Georgia,serif', fontSize:'28px', fontWeight:700, color:'#111827', marginBottom:'4px', letterSpacing:'-0.01em' }}>
            {E ? 'Enrolment Applications' : 'Permohonan Pendaftaran'}
          </h1>
          <p style={{ fontSize:'13px', color:'#6B7280' }}>
            {total > 0 ? `${total} ${E?'applications':'permohonan'}` : (E?'No applications found':'Tiada permohonan dijumpai')}
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          style={{ background:'none', border:'1.5px solid #E5E7EB', borderRadius:'8px', padding:'8px 16px', fontSize:'13px', fontWeight:600, cursor:'pointer', color:'#374151', fontFamily:'"Noto Sans",sans-serif', display:'flex', alignItems:'center', gap:'6px' }}
        >
          {loading ? '…' : '↻'} {E?'Refresh':'Muat Semula'}
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ background:'#FFFFFF', border:'1px solid #E5E7EB', borderRadius:'10px', padding:'14px 16px', marginBottom:'16px', display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Search */}
        <form onSubmit={handleSearch} style={{ position:'relative', flex:'1 1 200px' }}>
          <span style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'14px', color:'#9CA3AF' }}>🔍</span>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={E?'Search name, IC, number…':'Cari nama, IC, nombor…'}
            style={{ width:'100%', background:'#F9FAFB', border:'1.5px solid #E5E7EB', borderRadius:'7px', padding:'8px 12px 8px 34px', fontSize:'13px', color:'#111827', fontFamily:'"Noto Sans",sans-serif', outline:'none' }}
            onFocus={e=>{ e.target.style.borderColor='#F7D000'; e.target.style.boxShadow='0 0 0 3px rgba(247,208,0,0.15)'; }}
            onBlur={e=>{ e.target.style.borderColor='#E5E7EB'; e.target.style.boxShadow='none'; }}
          />
        </form>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); handleFilterChange(); }}
          style={{ background:'#F9FAFB', border:'1.5px solid #E5E7EB', borderRadius:'7px', padding:'8px 12px', fontSize:'13px', color:'#374151', fontFamily:'"Noto Sans",sans-serif', outline:'none', cursor:'pointer' }}
        >
          <option value="">{E?'All Statuses':'Semua Status'}</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{E ? l.en : l.ms}</option>
          ))}
        </select>

        {/* Level filter */}
        <select
          value={filterLevel}
          onChange={(e) => { setFilterLevel(e.target.value); handleFilterChange(); }}
          style={{ background:'#F9FAFB', border:'1.5px solid #E5E7EB', borderRadius:'7px', padding:'8px 12px', fontSize:'13px', color:'#374151', fontFamily:'"Noto Sans",sans-serif', outline:'none', cursor:'pointer' }}
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>{l || (E?'All Levels':'Semua Aras')}</option>
          ))}
        </select>

        {/* Active filters */}
        {(filterStatus || filterLevel || filterSearch) && (
          <button
            onClick={() => { setFilterStatus(''); setFilterLevel(''); setFilterSearch(''); setSearchInput(''); setPage(1); }}
            style={{ background:'#FEF2F2', border:'1px solid #B91C1C33', borderRadius:'999px', padding:'5px 12px', fontSize:'12px', fontWeight:600, cursor:'pointer', color:'#B91C1C', fontFamily:'"Noto Sans",sans-serif' }}
          >
            ✕ {E?'Clear filters':'Kosongkan penapis'}
          </button>
        )}
      </div>

      {/* Content */}
      {error ? (
        <ErrorBanner message={error} onRetry={() => void load()} />
      ) : loading && data.length === 0 ? (
        <LoadingState />
      ) : data.length === 0 ? (
        <EmptyState filtered={!!(filterStatus || filterLevel || filterSearch)} />
      ) : (
        <>
          {/* Row count */}
          <p style={{ fontSize:'12px', color:'#6B7280', marginBottom:'8px' }}>
            {E?`Showing ${(page-1)*LIMIT+1}–${Math.min(page*LIMIT,total)} of ${total} records`:`Menunjukkan ${(page-1)*LIMIT+1}–${Math.min(page*LIMIT,total)} daripada ${total} rekod`}
          </p>

          <div style={{ background:'#FFFFFF', border:'1px solid #E5E7EB', borderRadius:'10px', overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', opacity:loading?0.7:1, transition:'opacity 0.2s' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                  {[
                    E?'App. No.':'No. Permohonan',
                    E?'Student':'Pelajar',
                    E?'Level':'Aras',
                    E?'Guardian':'Wali',
                    E?'School':'Sekolah',
                    'Status',
                    E?'Date':'Tarikh',
                    '',
                  ].map((h) => (
                    <th key={h} style={{ padding:'11px 16px', textAlign:'left', fontSize:'11px', fontWeight:700, color:'#6B7280', whiteSpace:'nowrap', letterSpacing:'0.07em', textTransform:'uppercase', fontFamily:'"Noto Sans",sans-serif' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((app, i) => {
                  const sl = STATUS_LABEL[app.status];
                  return (
                    <tr
                      key={app.id}
                      style={{ borderBottom: i < data.length - 1 ? '1px solid #F3F4F6' : 'none', transition:'background 0.08s' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background='#FFFAED'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background=''; }}
                    >
                      <td style={tdStyle()}>
                        <span style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:'12px', color:'#6B7280' }}>{app.application_number}</span>
                      </td>
                      <td style={tdStyle()}>
                        <div style={{ fontWeight:600, color:'#111827', fontSize:'14px' }}>{app.student_full_name_en}</div>
                        {app.student_ic_number && (
                          <div style={{ fontSize:'11px', color:'#9CA3AF', fontFamily:'"JetBrains Mono","Courier New",monospace' }}>{app.student_ic_number.slice(0,-4).replace(/\d/g,'•')}{app.student_ic_number.slice(-4)}</div>
                        )}
                      </td>
                      <td style={tdStyle()}>
                        <span style={{ fontSize:'12px', color:'#374151', textTransform:'capitalize' }}>{app.applied_level ?? '—'}</span>
                      </td>
                      <td style={tdStyle()}>
                        <div style={{ fontSize:'13px', color:'#111827', fontWeight:500 }}>{app.guardian_name}</div>
                        <div style={{ fontSize:'11px', color:'#9CA3AF' }}>{app.guardian_phone}</div>
                      </td>
                      <td style={tdStyle()}>
                        <span style={{ fontSize:'12px', color:'#374151' }}>{app.school_name ?? '—'}</span>
                      </td>
                      <td style={tdStyle()}>
                        <span style={{
                          display:'inline-block', padding:'3px 10px',
                          background: sl ? `${sl.color}18` : '#F3F4F6',
                          color: sl?.color ?? '#6B7280',
                          borderRadius:'999px',
                          fontSize:'11px', fontWeight:700,
                          fontFamily:'"Noto Sans",sans-serif',
                        }}>
                          {(E ? sl?.en : sl?.ms) ?? app.status}
                        </span>
                      </td>
                      <td style={{ ...tdStyle(), fontSize:'12px', color:'#9CA3AF' }}>
                        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString(E?'en-GB':'ms-BN') : '—'}
                      </td>
                      <td style={tdStyle()}>
                        <Link
                          to={`/admin/applications/${app.id}`}
                          style={{ color:'#1D4ED8', fontSize:'12px', fontWeight:600, textDecoration:'none', whiteSpace:'nowrap' }}
                        >
                          {E?'View →':'Lihat →'}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'8px', marginTop:'20px' }}>
              <button
                onClick={() => setPage(p => p - 1)} disabled={page <= 1 || loading}
                style={{ background:'none', border:'1.5px solid #E5E7EB', borderRadius:'7px', padding:'7px 14px', fontSize:'13px', fontWeight:600, cursor:page<=1?'not-allowed':'pointer', color:'#374151', opacity:page<=1?0.4:1 }}
              >
                ← {E?'Previous':'Sebelum'}
              </button>
              {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                const p = page<=3?i+1:i+page-2;
                if(p<1||p>totalPages)return null;
                return (
                  <button key={p} onClick={()=>setPage(p)} style={{ width:'36px', height:'36px', border:'1.5px solid #E5E7EB', borderRadius:'7px', fontSize:'13px', fontWeight:p===page?700:500, background:p===page?'#F7D000':'none', color:p===page?'#111827':'#374151', cursor:'pointer' }}>
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => p + 1)} disabled={page >= totalPages || loading}
                style={{ background:'none', border:'1.5px solid #E5E7EB', borderRadius:'7px', padding:'7px 14px', fontSize:'13px', fontWeight:600, cursor:page>=totalPages?'not-allowed':'pointer', color:'#374151', opacity:page>=totalPages?0.4:1 }}
              >
                {E?'Next':'Seterus'} →
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function selectStyle(): React.CSSProperties {
  return {
    background:'#F9FAFB', border:'1.5px solid #E5E7EB',
    borderRadius:'7px', padding:'8px 12px',
    fontSize:'13px', color:'#374151',
    fontFamily:'"Noto Sans",sans-serif',
    outline:'none', cursor:'pointer',
  };
}

function tdStyle(): React.CSSProperties {
  return {
    padding:'13px 16px',
    fontSize:'13px', color:'#374151',
    verticalAlign:'middle',
  };
}

function LoadingState() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}>
      <Spinner size="lg" />
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.neutral.gray400 }}>
      <div style={{ fontSize: '40px', marginBottom: spacing.md }}>📋</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
        {filtered ? 'Tiada permohonan sepadan' : 'Tiada permohonan lagi'}
      </p>
      <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm }}>
        {filtered ? 'No applications match your filters.' : 'No applications have been submitted yet.'}
      </p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{
      backgroundColor: 'rgba(239,68,68,0.08)',
      border:          '1px solid rgba(239,68,68,0.3)',
      borderRadius:    borderRadius.lg,
      padding:         spacing.lg,
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'space-between',
      gap:             spacing.md,
    }}>
      <div>
        <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: '#f87171' }}>Ralat / Error</p>
        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{message}</p>
      </div>
      <Button variant="secondary" size="sm" onClick={onRetry}>Cuba Lagi / Retry</Button>
    </div>
  );
}
